import {
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";

import { UploadErrorAlert } from "@/components/upload/UploadErrorAlert";
import { useUploadFiles } from "@/components/upload/useUploadFiles";
import { cx } from "@/lib/utils";

interface CompactUploadDropzoneProps {
  maxBytes?: number;
  maxFiles?: number;
  maxTotalBytes?: number;
  disabled?: boolean;
  onFilesChosen: (files: File[]) => void;
  errorMessage?: string | null;
  /** External status label ("analyzing…") shown in place of the title. */
  statusLabel?: string | null;
  /** Allowed file extensions including the leading dot (lowercase). */
  acceptedFileTypes?: readonly string[];
  /** Override the default i18n title ("Přidat další díl"). */
  title?: string;
  /** Override the default i18n hint ("Přetáhněte…"). */
  hint?: string;
  /** Inline style overrides for the icon badge — useful to recolour
   *  per-process tone when the dropzone lives inside an OperationSection. */
  iconStyle?: { background?: string; color?: string };
}

const DEFAULT_MAX_BYTES = 20 * 1024 * 1024;
const DEFAULT_MAX_FILES = 25;
const DEFAULT_MAX_TOTAL_BYTES = 100 * 1024 * 1024;
const DEFAULT_ACCEPTED: readonly string[] = [".dxf"];

/**
 * Compact "add another part" dropzone — horizontal row variant used in
 * the cart underneath the items list. Shares client-side validation
 * with ``UploadDropzone`` (hero variant on Landing) via
 * ``useUploadFiles``; differs only in layout: a single row with a small
 * icon, title + hint, and format chips on the right. The whole row is
 * the click + drop target — no separate "pick file" button — because
 * the user is already inside the funnel and doesn't need a second hero
 * CTA pulling weight.
 */
export function CompactUploadDropzone({
  maxBytes = DEFAULT_MAX_BYTES,
  maxFiles = DEFAULT_MAX_FILES,
  maxTotalBytes = DEFAULT_MAX_TOTAL_BYTES,
  disabled = false,
  onFilesChosen,
  errorMessage,
  statusLabel,
  acceptedFileTypes = DEFAULT_ACCEPTED,
  title,
  hint,
  iconStyle,
}: CompactUploadDropzoneProps): JSX.Element {
  const { t: tCart } = useTranslation("cart");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const { handleFiles, localError, maxMb } = useUploadFiles({
    maxBytes,
    maxFiles,
    maxTotalBytes,
    acceptedFileTypes,
    disabled,
    onFilesChosen,
  });

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker();
    }
  };

  const displayedError = errorMessage ?? localError;
  const acceptedLabel = acceptedFileTypes.join(" / ");

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label={tCart("addMore")}
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cx(
          "flex items-center gap-3 rounded-md border border-dashed px-4 py-3 transition",
          "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          dragOver
            ? "border-ink bg-surface"
            : "border-border-strong bg-surface-2 hover:border-ink hover:bg-surface",
          disabled && "pointer-events-none opacity-60",
          displayedError && "border-danger",
        )}
        data-testid="upload-dropzone-compact"
      >
        <div
          aria-hidden="true"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-bg text-accent-ink"
          style={iconStyle}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.75}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 7.5 7.5 12M12 7.5v12"
            />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink">
            {statusLabel ?? title ?? tCart("addMore")}
          </p>
          {!statusLabel && (
            <p className="text-xs text-ink-3">
              {hint ?? tCart("addMoreHint", { accepted: acceptedLabel, maxMb })}
            </p>
          )}
        </div>
        <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
          {acceptedFileTypes.map((ext) => (
            <span
              key={ext}
              className="rounded-md border border-border bg-surface px-2 py-0.5 text-[11px] font-medium uppercase text-ink-2"
            >
              {ext.replace(/^\./, "")}
            </span>
          ))}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={acceptedFileTypes.join(",")}
          multiple
          onChange={handleInputChange}
          onClick={(e) => e.stopPropagation()}
          className="hidden"
          disabled={disabled}
          data-testid="upload-input-compact"
        />
      </div>

      {displayedError && (
        <UploadErrorAlert
          message={displayedError}
          className="mt-2"
          testId="upload-error-compact"
        />
      )}
    </div>
  );
}
